import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { api } from "../api";
import type { WorkspaceSummary } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { CreateWorkspaceDialog } from "../components/CreateWorkspaceDialog";
import { Button } from "../compose/controls";
import { LogOut, Plus } from "../compose/Icon";
import { ErrorState, Skeleton } from "../compose/states";
import { resolveAccent } from "../lib/accents";
import { useAsync } from "../lib/useAsync";
import { useTheme } from "../theme/ThemeProvider";
import { BrandWordmark } from "../shell/BrandWordmark";

/**
 * Workspace picker `/` (design screens §2). A card per membership, each
 * previewing its OWN accent as a spine + swatch — the only place a workspace's
 * colour appears before you enter. Counts (delivered / draft / superseded)
 * answer the new-hire "what's here?" immediately. Single membership auto-enters.
 */
export function WorkspacePicker() {
  const { logout, user } = useAuth();
  const { resolved } = useTheme();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const { data, loading, error } = useAsync(() => api.listWorkspaces(), []);

  if (!loading && data && data.length === 1) {
    return <Navigate to={`/w/${data[0].slug}`} replace />;
  }

  const accentColor = (w: WorkspaceSummary) => {
    const a = resolveAccent(w.accent);
    return resolved === "dark" ? a.dark : a.light;
  };

  return (
    <div className="picker">
      <header className="picker__top">
        <BrandWordmark className="picker__logo" height={24} />
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {user?.is_staff ? (
            <Button
              variant="primary"
              data-testid="new-workspace"
              icon={<Plus size={15} strokeWidth={1.5} />}
              onClick={() => setCreateOpen(true)}
            >
              New workspace
            </Button>
          ) : null}
          <Button
            variant="ghost"
            data-testid="picker-logout"
            icon={<LogOut size={15} strokeWidth={1.5} />}
            onClick={async () => {
              await logout();
              navigate("/login", { replace: true });
            }}
          >
            Sign out
          </Button>
        </div>
      </header>

      <div className="picker__body">
        <h1 className="page__title" style={{ fontSize: 28 }}>
          Choose a workspace
        </h1>
        <p className="page__sub">Pick a room to step into.</p>

        {loading ? (
          <div className="wsgrid">
            {[0, 1].map((i) => (
              <Skeleton key={i} height={170} radius={8} />
            ))}
          </div>
        ) : error ? (
          <ErrorState body="We couldn't load your workspaces. Try refreshing." />
        ) : (
          <div className="wsgrid" data-testid="workspace-grid">
            {data?.map((w) => (
              <Link
                key={w.slug}
                to={`/w/${w.slug}`}
                className="wscard"
                data-testid={`workspace-card-${w.slug}`}
                style={
                  { "--wscard-accent": accentColor(w) } as React.CSSProperties
                }
              >
                <div className="wscard__swatch" aria-hidden />
                <h2 className="wscard__name">{w.name}</h2>
                <p className="wscard__client">{w.client}</p>
                <div className="wscard__counts">
                  <span className="wscount">
                    <span className="wscount__n wscount__n--delivered">
                      {w.doc_counts.delivered}
                    </span>
                    <span className="wscount__l">Delivered</span>
                  </span>
                  <span className="wscount">
                    <span className="wscount__n wscount__n--draft">
                      {w.doc_counts.draft}
                    </span>
                    <span className="wscount__l">Draft</span>
                  </span>
                  <span className="wscount">
                    <span className="wscount__n">{w.doc_counts.superseded}</span>
                    <span className="wscount__l">Superseded</span>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {createOpen ? (
        <CreateWorkspaceDialog
          onClose={() => setCreateOpen(false)}
          onCreated={(ws) => {
            setCreateOpen(false);
            navigate(`/w/${ws.slug}`);
          }}
        />
      ) : null}
    </div>
  );
}
